// src/components/landing/HeroStream.tsx
//
// Hero visual — a live-feeling stream of transactions being matched or
// flagged, built from MessageScroller (auto-scrolling, performant list
// container) + Bubble (individual transaction cards). Neither component
// was built for this — MessageScroller is a chat-feed primitive, Bubble
// is a chat-message primitive — but the underlying grammar (small,
// independently-rendered items arriving in a continuous, auto-scrolling
// stream, each carrying a status) maps directly onto "every transaction
// gets a first read."
//
// FIXED — useMessageScroller (called internally by Viewport/Content)
// reads context from MessageScrollerProvider, not from MessageScroller
// (Root) alone. Root and Provider are separate parts; the whole tree
// must be wrapped in Provider or the hook throws — this failed the
// prerender build with "useMessageScroller must be used within a
// MessageScroller" because Provider was missing entirely.
//
// Purely decorative/illustrative — same "example detection, not a real
// customer case" caveat as the Proof section. Loops a fixed, fake feed.
//
// UPDATED — accepts an optional `feed` prop so a second vertical
// (search-performance detection, ContentTeamsView) can reuse the same
// scroller mechanics with its own item set, rather than forking the
// whole component. Defaults to the original Reconciliation/Legal FEED
// so LandingView's existing usage (no feed prop passed) is unchanged.

import * as React from 'react';
import {
  MessageScrollerProvider,
  MessageScroller,
  MessageScrollerViewport,
  MessageScrollerContent,
  MessageScrollerItem,
} from '../ui/message-scroller';
import { Bubble, BubbleContent } from '../ui/bubble';
import { Check, AlertTriangle } from 'lucide-react';

export type FeedItem = {
  id: string;
  label: string;
  amount: string;
  status: 'matched' | 'flagged';
  detail?: string;
};

// Fixed, fake, illustrative feed — cycles on a timer, not a real data
// source. Mirrors the Proof section's example ($1,200 / $1,540 / 28%)
// so the hero and Proof section feel like the same mechanism, not two
// unrelated demos.
const FEED: FeedItem[] = [
  { id: '1', label: 'Card auth', amount: '$1,200.00', status: 'matched' },
  { id: '2', label: 'Vendor payment', amount: '$412.50', status: 'matched' },
  { id: '3', label: 'Card clearing', amount: '$1,540.00', status: 'flagged', detail: '28% over authorized' },
  { id: '4', label: 'Expense report', amount: '$89.00', status: 'matched' },
  { id: '5', label: 'Subscription charge', amount: '$29.00', status: 'matched' },
  { id: '6', label: 'Contract clause', amount: 'Auto-renewal', status: 'flagged', detail: 'Non-standard term' },
  { id: '7', label: 'Payroll run', amount: '$14,220.00', status: 'matched' },
  { id: '8', label: 'Reimbursement', amount: '$156.30', status: 'matched' },
];

function FeedBubble({ item }: { item: FeedItem }) {
  const isFlagged = item.status === 'flagged';
  return (
    <Bubble align="start" variant={isFlagged ? 'tinted' : 'outline'} className="max-w-full">
      <BubbleContent className="flex items-center gap-3 px-4 py-2.5">
        <div
          className={
            isFlagged
              ? 'shrink-0 w-6 h-6 rounded-full bg-brand-accent/10 flex items-center justify-center'
              : 'shrink-0 w-6 h-6 rounded-full bg-brand-surface flex items-center justify-center'
          }
        >
          {isFlagged ? (
            <AlertTriangle className="w-3.5 h-3.5 text-brand-accent" strokeWidth={2} />
          ) : (
            <Check className="w-3.5 h-3.5 text-brand-black" strokeWidth={2} />
          )}
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-xs font-medium text-brand-black truncate">{item.label}</span>
          <span className="text-[11px] text-brand-gray font-mono">
            {item.amount}
            {item.detail && <span className="text-brand-accent"> — {item.detail}</span>}
          </span>
        </div>
      </BubbleContent>
    </Bubble>
  );
}

export const HeroStream: React.FC<{ feed?: FeedItem[] }> = ({ feed = FEED }) => {
  // Loops the given feed continuously by re-appending a shuffled copy
  // once the visible list grows past a cap — keeps the scroller feeling
  // "live" without needing a real backend.
  const [items, setItems] = React.useState<FeedItem[]>(feed.slice(0, 3));

  React.useEffect(() => {
    // Reset to the new feed's first items whenever the feed prop
    // itself changes (e.g. navigating between LandingView and
    // ContentTeamsView, if they ever share a mounted instance) —
    // without this, switching feeds mid-loop would keep showing stale
    // items from the previous feed until the cap naturally scrolled
    // them out.
    setItems(feed.slice(0, 3));

    let i = 3;
    const interval = setInterval(() => {
      setItems((prev) => {
        const next = [...prev, { ...feed[i % feed.length], id: `${feed[i % feed.length].id}-${i}` }];
        i += 1;
        return next.length > 20 ? next.slice(next.length - 20) : next;
      });
    }, 1800);
    return () => clearInterval(interval);
  }, [feed]);

  return (
    <div className="relative w-full max-w-2xl h-[360px] rounded-2xl border border-brand-border bg-brand-surface/40 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-brand-surface/40 to-transparent z-10 pointer-events-none" />
      <MessageScrollerProvider autoScroll>
        <MessageScroller className="h-full">
          <MessageScrollerViewport className="no-scrollbar [scrollbar-gutter:auto] px-4 py-6">
            <MessageScrollerContent className="gap-3">
              {items.map((item) => (
                <MessageScrollerItem key={item.id} messageId={item.id}>
                  <FeedBubble item={item} />
                </MessageScrollerItem>
              ))}
            </MessageScrollerContent>
          </MessageScrollerViewport>
        </MessageScroller>
      </MessageScrollerProvider>
      <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-brand-surface/40 to-transparent pointer-events-none" />
    </div>
  );
};